/**
 * One summary per prebid auction, sent to Datadog RUM as the `prebid_auction`
 * duration vital (auctionInit to auctionEnd) next to `first_ad_visible`, so a
 * slow first ad can be traced to its auction. Built only from prebid's own
 * events: the tracker copies a few scalars off each payload and never changes
 * it. Per-bidder detail stays in AY.
 *
 * Context on each summary:
 *
 * - `auction_kind`: `first` for the view's first auction, `refresh` for every
 *   later one (AY refreshes and changePage).
 * - `prebid_timeout_ms`: the timeout on the auction itself, which
 *   `requestBids` can set apart from the config.
 * - `bidder_count`, `ad_unit_count`, `bids_received_count` and
 *   `rejected_bid_count`.
 * - `timed_out_bidder_count`: bidders prebid reported in a `bidTimeout`
 *   event. `unaccounted_bidder_count`: bidders that by the end neither bid,
 *   said they had no bid, had a bid rejected nor timed out.
 * - `intent_to_init_ms`: the page load's first ad request to the first
 *   auction's auctionInit, on the landing view only. Negative when the auction
 *   started before any provider asked for an ad.
 * - `init_to_bid_request_ms`: auctionInit to the first bidder request.
 * - `end_to_targeting_ms`: auctionEnd to the moment targeting was set from
 *   this auction: the targeted bid is one of its bids, or no other auction of
 *   the view used that ad unit. Left out when it can't be told which auction
 *   it was.
 * - `auction_number` (1 for the view's first auction) and `auctions_dropped`:
 *   auctions of the view not sent before this one.
 *
 * When a view ends (a navigation, the page going to the background or being
 * left), a finished auction still waiting for its targeting is sent without
 * it, the unfinished ones are dropped, and one
 * `prebid_auction_summary` vital carries the view's final
 * `prebid_auctions_seen`, `prebid_auctions_sent` and `prebid_auctions_dropped`,
 * once per view. The same counts are kept on the view's context as they change
 * (`onCounts`) for live inspection, but view context updates are throttled and
 * can miss the exit flush, so the summary vital is the one to count on.
 * After its summary a view takes nothing more: auctions of a page that comes
 * back from the background are neither sent nor counted, until a path change
 * starts a new view.
 *
 * Same definition as Qz Core, so the Discover NTP and the articles it opens
 * report it the same way. Wired up in plugins/prebid-auction.client.ts.
 */

export const PREBID_AUCTION_VITAL = 'prebid_auction'

/** One per view, from its start to its end, with the view's final counts. */
export const PREBID_AUCTION_SUMMARY_VITAL = 'prebid_auction_summary'

/** Summaries sent per view. Later auctions are counted as dropped. */
export const MAX_AUCTIONS_PER_VIEW = 5

/** Auction ids remembered to tell a replayed event from its live copy. */
const MAX_SEEN_IDS = 50

/** Ad units remembered per view, to tell whether only one auction used an ad unit. */
const MAX_AD_UNITS_PER_VIEW = 100

/** How long a finished auction waits for its targeting before it is sent without it. */
export const TARGETING_WAIT_MS = 3_000

/** Past this, an auction no longer says anything about how the page loaded. Same cap as `first_ad_visible`. */
export const PREBID_AUCTION_MAX_MS = 60_000

/** The prebid events the summary is built from. */
export const PREBID_AUCTION_EVENTS = [
  'auctionInit',
  'bidRequested',
  'bidTimeout',
  'auctionEnd',
  'setTargeting',
] as const
export type PrebidAuctionEvent = (typeof PREBID_AUCTION_EVENTS)[number]

export interface PrebidAuctionSample {
  /** Epoch ms of the auctionInit. */
  startTime: number
  /** Ms from auctionInit to auctionEnd. */
  duration: number
  /** Whether the auction ran on the page load's landing view. */
  landingView: boolean
  context: Record<string, string | number>
}

/** The current view's auctions so far. Seen less sent and dropped are still running. */
export interface PrebidAuctionCounts {
  seen: number
  sent: number
  dropped: number
  landingView: boolean
}

/** Why a view stopped: a real navigation, the page going to the background, or the page being left. */
export type PrebidAuctionViewEnd = 'navigation' | 'hidden' | 'exit'

export interface PrebidAuctionTracker {
  /** Takes one prebid event that happened at `at`, ms on the `performance.now()` clock. */
  handle: (event: PrebidAuctionEvent, args: unknown, at: number) => void
  /**
   * The view stopped being one the auctions can describe, at `at`. Auctions
   * still running are dropped and, the first
   * time only, the view's final counts are sent. Call it while the view is
   * still the SDK's current one.
   */
  endView: (reason: PrebidAuctionViewEnd, at: number) => void
  /** A new view started at `at`. Call it once the SDK is on the new view. Earlier events are ignored. */
  startView: (at: number) => void
}

interface Auction {
  number: number
  kind: 'first' | 'refresh'
  initAt: number
  timeout: number | undefined
  adUnitCodes: Set<string>
  bidders: Set<string>
  firstBidRequestAt?: number
  timedOutBidders: Set<string>
  endAt?: number
  bidsReceived?: number
  rejectedBids?: number
  unaccountedBidders?: number
  // The ids of its bids, only to tell which auction a targeting came from.
  // Never sent.
  adIds: Set<string>
  timer?: ReturnType<typeof setTimeout>
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null
const listOf = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []
const textOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined
const numberOf = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined
const field = (value: unknown, key: string): unknown =>
  isRecord(value) ? value[key] : undefined
// GPT targeting values are strings or lists of strings.
const firstTextOf = (value: unknown): string | undefined =>
  textOf(Array.isArray(value) ? value[0] : value)

const namesOf = (items: unknown, key: string): Set<string> => {
  const names = new Set<string>()
  for (const item of listOf(items)) {
    const name = textOf(field(item, key))
    if (name) names.add(name)
  }
  return names
}

export function createPrebidAuctionTracker({
  timeOrigin,
  intentAt,
  isHidden,
  send,
  sendSummary,
  onCounts,
}: {
  /** `performance.timeOrigin`, to turn event times into epoch ms. */
  timeOrigin: number
  /** When the page load's first ad request was made, on the `performance.now()` clock, or undefined before it. */
  intentAt: () => number | undefined
  isHidden: () => boolean
  send: (sample: PrebidAuctionSample) => void
  /** Sends the view's final counts, the `prebid_auction_summary` vital. */
  sendSummary: (sample: PrebidAuctionSample) => void
  /** Gets the view's counts whenever they change. */
  onCounts?: (counts: PrebidAuctionCounts) => void
}): PrebidAuctionTracker {
  // Recent auction ids, so a replayed event and its live copy count once.
  // Oldest first.
  const seen = new Set<string>()
  const running = new Map<string, Auction>()
  let landingView = true
  let viewStartedAt = -Infinity
  let seenOnView = 0
  let sentOnView = 0
  let droppedOnView = 0
  let viewEnded = false
  // How many auctions of the view used each ad unit.
  const adUnitUse = new Map<string, number>()

  const reportCounts = () => {
    try {
      onCounts?.({
        seen: seenOnView,
        sent: sentOnView,
        dropped: droppedOnView,
        landingView,
      })
    } catch {
      // Lose the counts, keep the summaries.
    }
  }
  const dropped = () => {
    droppedOnView++
    reportCounts()
  }

  const finish = (id: string, targetingAt?: number) => {
    const auction = running.get(id)
    if (!auction || auction.endAt === undefined) return
    clearTimeout(auction.timer)
    running.delete(id)
    const duration = auction.endAt - auction.initAt
    if (duration < 0 || duration > PREBID_AUCTION_MAX_MS) {
      dropped()
      return
    }
    const intent =
      auction.kind === 'first' && landingView ? intentAt() : undefined
    send({
      startTime: Math.round(timeOrigin + auction.initAt),
      duration,
      landingView,
      context: {
        auction_kind: auction.kind,
        auction_number: auction.number,
        auctions_dropped: droppedOnView,
        ...(auction.timeout === undefined
          ? {}
          : { prebid_timeout_ms: auction.timeout }),
        bidder_count: auction.bidders.size,
        ad_unit_count: auction.adUnitCodes.size,
        timed_out_bidder_count: auction.timedOutBidders.size,
        unaccounted_bidder_count: auction.unaccountedBidders ?? 0,
        bids_received_count: auction.bidsReceived ?? 0,
        rejected_bid_count: auction.rejectedBids ?? 0,
        ...(intent === undefined
          ? {}
          : { intent_to_init_ms: Math.round(auction.initAt - intent) }),
        ...(auction.firstBidRequestAt === undefined
          ? {}
          : {
              init_to_bid_request_ms: Math.round(
                auction.firstBidRequestAt - auction.initAt
              ),
            }),
        ...(targetingAt === undefined
          ? {}
          : { end_to_targeting_ms: Math.round(targetingAt - auction.endAt) }),
      },
    })
    sentOnView++
    reportCounts()
  }

  const onInit = (args: unknown, at: number) => {
    const id = textOf(field(args, 'auctionId'))
    if (!id || seen.has(id)) return
    seen.add(id)
    if (seen.size > MAX_SEEN_IDS) seen.delete(seen.values().next().value ?? '')
    seenOnView++
    const codes = namesOf(field(args, 'adUnits'), 'code')
    for (const code of listOf(field(args, 'adUnitCodes'))) {
      const name = textOf(code)
      if (name) codes.add(name)
    }
    for (const code of codes) {
      if (adUnitUse.has(code) || adUnitUse.size < MAX_AD_UNITS_PER_VIEW)
        adUnitUse.set(code, (adUnitUse.get(code) ?? 0) + 1)
    }
    if (seenOnView > MAX_AUCTIONS_PER_VIEW || isHidden()) {
      dropped()
      return
    }
    running.set(id, {
      number: seenOnView,
      kind: seenOnView === 1 ? 'first' : 'refresh',
      initAt: at,
      timeout: numberOf(field(args, 'timeout')),
      adUnitCodes: codes,
      bidders: namesOf(field(args, 'bidderRequests'), 'bidderCode'),
      timedOutBidders: new Set(),
      adIds: new Set(),
    })
    reportCounts()
  }

  const onBidRequested = (args: unknown, at: number) => {
    const auction = running.get(textOf(field(args, 'auctionId')) ?? '')
    if (
      auction &&
      auction.firstBidRequestAt === undefined &&
      auction.endAt === undefined
    )
      auction.firstBidRequestAt = at
  }

  const onBidTimeout = (args: unknown) => {
    for (const bid of listOf(args)) {
      const auction = running.get(textOf(field(bid, 'auctionId')) ?? '')
      const bidder = textOf(field(bid, 'bidder'))
      if (auction && bidder) auction.timedOutBidders.add(bidder)
    }
  }

  const onEnd = (args: unknown, at: number) => {
    const id = textOf(field(args, 'auctionId'))
    const auction = id ? running.get(id) : undefined
    if (!id || !auction || auction.endAt !== undefined) return
    auction.endAt = at
    const bidsReceived = field(args, 'bidsReceived')
    const bidsRejected = field(args, 'bidsRejected')
    auction.bidsReceived = listOf(bidsReceived).length
    auction.rejectedBids = listOf(bidsRejected).length
    auction.adIds = namesOf(bidsReceived, 'adId')
    const accounted = new Set([
      ...namesOf(bidsReceived, 'bidderCode'),
      ...namesOf(field(args, 'noBids'), 'bidder'),
      ...namesOf(bidsRejected, 'bidderCode'),
      ...auction.timedOutBidders,
    ])
    auction.unaccountedBidders = [...auction.bidders].filter(
      (bidder) => !accounted.has(bidder)
    ).length
    auction.timer = setTimeout(() => finish(id), TARGETING_WAIT_MS)
  }

  // Targeting comes from the auction that holds the targeted bid. Without a bid
  // id, it can only be told when a single
  // auction of the whole view used that ad unit.
  const onTargeting = (args: unknown, at: number) => {
    if (!isRecord(args)) return
    const targeted = new Set<string>()
    for (const [code, targeting] of Object.entries(args)) {
      const adId = firstTextOf(field(targeting, 'hb_adid'))
      const onUnit = [...running].filter(([, auction]) =>
        auction.adUnitCodes.has(code)
      )
      let match =
        adUnitUse.get(code) === 1 && onUnit.length === 1 ? onUnit[0] : undefined
      if (adId) match = onUnit.find(([, auction]) => auction.adIds.has(adId))
      if (match && match[1].endAt !== undefined) targeted.add(match[0])
    }
    for (const id of targeted) finish(id, at)
  }

  return {
    handle(event, args, at) {
      // A measurement only: a payload it can't read loses the summary, never
      // reaches prebid.
      try {
        // An event from before the view started belongs to the previous one.
        if (at < viewStartedAt) return
        // Once its final counts went out, the view takes nothing more until a
        // new one starts.
        if (viewEnded) return
        switch (event) {
          case 'auctionInit':
            onInit(args, at)
            break
          case 'bidRequested':
            onBidRequested(args, at)
            break
          case 'bidTimeout':
            onBidTimeout(args)
            break
          case 'auctionEnd':
            onEnd(args, at)
            break
          case 'setTargeting':
            onTargeting(args, at)
            break
        }
      } catch {
        // Lose the measurement, keep the auction.
      }
    },
    endView(reason, at) {
      try {
        // A finished auction only waiting for its targeting goes out without
        // it. Only the unfinished ones are dropped.
        for (const [id, auction] of [...running]) {
          if (auction.endAt !== undefined) finish(id)
        }
        if (running.size > 0) {
          for (const auction of running.values()) clearTimeout(auction.timer)
          droppedOnView += running.size
          running.clear()
          reportCounts()
        }
        if (viewEnded) return
        viewEnded = true
        if (seenOnView === 0) return
        const start = Math.max(0, viewStartedAt)
        sendSummary({
          startTime: Math.round(timeOrigin + start),
          duration: Math.max(0, at - start),
          landingView,
          context: {
            view_end: reason,
            prebid_auctions_seen: seenOnView,
            prebid_auctions_sent: sentOnView,
            prebid_auctions_dropped: droppedOnView,
          },
        })
      } catch {
        // Lose the counts, keep the page.
      }
    },
    startView(at) {
      for (const auction of running.values()) clearTimeout(auction.timer)
      running.clear()
      adUnitUse.clear()
      landingView = false
      viewEnded = false
      viewStartedAt = at
      seenOnView = 0
      sentOnView = 0
      droppedOnView = 0
      reportCounts()
    },
  }
}
